import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { StrikerBrand } from '@striker/ui-shell';
import { useAuth } from '../auth/AuthContext';

export function LoginPage() {
  const { login } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sosOpen, setSosOpen] = useState(false);
  const [sosEmail, setSosEmail] = useState('');
  const [sosMsg, setSosMsg] = useState<string | null>(null);
  const [sosBusy, setSosBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(identifier, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onSosRequest(event: FormEvent) {
    event.preventDefault();
    setSosBusy(true);
    setSosMsg(null);
    try {
      const res = await fetch('/api/v1/public/support/sos/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: sosEmail || identifier }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        message?: string;
        debug_unlock_link?: string;
      };
      if (!res.ok) throw new Error(body.message || `Richiesta SOS fallita (${res.status})`);
      setSosMsg(body.message || 'Se l’email è registrata, riceverai un link sicuro.');
    } catch (err) {
      setSosMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSosBusy(false);
    }
  }

  return (
    <div className="sk-login">
      <form className="sk-login__card" onSubmit={onSubmit}>
        <div className="sk-login__brand">
          <StrikerBrand surface="dark" title="StrikerPos" />
        </div>
        <h1 className="sk-page__title">Accedi</h1>
        <p className="sk-page__lead">Inserisci le tue credenziali per accedere.</p>

        <label className="sk-field">
          <span>Email</span>
          <input
            className="sk-input"
            type="email"
            autoComplete="username"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            required
          />
        </label>
        <label className="sk-field">
          <span>Password</span>
          <input
            className="sk-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>

        {error ? <p className="sk-error">{error}</p> : null}

        <button type="submit" className="sk-btn sk-btn--block" disabled={busy}>
          {busy ? 'Accesso…' : 'Entra'}
        </button>

        <p style={{ textAlign: 'center', marginTop: '0.75rem' }}>
          <Link to="/forgot-password" style={{ fontSize: '0.875rem' }}>
            Password dimenticata?
          </Link>
        </p>

        <p style={{ textAlign: 'center', marginTop: '0.5rem' }}>
          <Link to="/register" style={{ fontSize: '0.875rem', fontWeight: 600 }}>
            Iscriviti come locale →
          </Link>
        </p>

        <p style={{ textAlign: 'center', marginTop: '0.5rem' }}>
          <Link to="/end-user/register" style={{ fontSize: '0.875rem' }}>
            Non sei un locale? Crea il tuo Passaporto StrikerPos →
          </Link>
        </p>

        <button
          type="button"
          className="sk-btn sk-btn--block"
          style={{ marginTop: '0.75rem' }}
          onClick={() => setSosOpen((v) => !v)}
        >
          {sosOpen ? 'Chiudi SOS' : 'Problemi di accesso? SOS'}
        </button>
      </form>

      {sosOpen ? (
        <form className="sk-login__card" onSubmit={onSosRequest} style={{ marginTop: '1rem' }}>
          <h2 className="sk-page__title" style={{ fontSize: '1.25rem' }}>
            SOS emergenza
          </h2>
          <p className="sk-page__lead">
            Invia un link sicuro (15 min) all’email aziendale registrata sul locale.
          </p>
          <label className="sk-field">
            <span>Email aziendale</span>
            <input
              className="sk-input"
              type="email"
              value={sosEmail || identifier}
              onChange={(e) => setSosEmail(e.target.value)}
              required
            />
          </label>
          {sosMsg ? <p className="sk-muted">{sosMsg}</p> : null}
          <button type="submit" className="sk-btn sk-btn--block" disabled={sosBusy}>
            {sosBusy ? 'Invio…' : 'Richiedi link SOS'}
          </button>
        </form>
      ) : null}
    </div>
  );
}
